import * as React from 'react';
import AccessibilityAnchorButton from 'mini-accessibility/lib/components/AccessibilityAnchorButton';
import { AccessibilityAnchorOrientation } from 'mini-accessibility/lib/schema/AccessibilityAnchorButtonProps';
import { getRTL } from '@uifabric/utilities';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';

export interface TouchState {
    started: boolean;
    isSwiping: boolean;
    startX: number;
    startY: number;
    moveX: number;
    leftAction: boolean;
    rightAction: boolean;
}

export interface TouchThreshold {
    swipe: number;
    action: number;
    edgeSwipe: number;
    longPress: number;
}

export enum SwipeStage {
    Cancelled = 0,
    Started,
    Completed,
}

export interface AccessibilityAnchorButtonParams<P> {
    labelGenerator: (props: P) => string;
    announcementGenerator: (props: P) => string;
    onClick?: (props: P) => void;
}

export enum AccessibilityButtonsPlacement {
    BEFORE,
    AFTER,
}

interface TouchBehaviourProps<P> {
    //Required
    Component: React.ComponentClass<P> | React.FC<P>;
    originalLeftSwipeRenderer: (
        props: P,
        state: TouchState,
        threshold: TouchThreshold
    ) => JSX.Element;
    originalRightSwipeRenderer: (
        props: P,
        state: TouchState,
        threshold: TouchThreshold
    ) => JSX.Element;
    originalGetLeftSwipeAction: () => (props: P) => void;
    originalGetRightSwipeAction: () => (props: P) => void;
    threshold: TouchThreshold;
    skipHandleTouchCallback: (moveX?: number, Component?: any, nativeEvent?: Event) => boolean;

    //Optional
    clickAction?: (props: P) => void;
    longPressAction?: (props: P) => void;
    originalLeftSwipeActionInitiated?: (props: P) => void;
    originalRightSwipeActionInitiated?: (props: P) => void;
    alwaysRenderSwipeRenderer?: boolean;
    enableWillChangeTransform?: boolean;
    shouldHandleRTLScenario?: boolean;
    notifyOnSwipe?: (props: P, swipeStage: SwipeStage) => void;
    leftAccessibilityButtonParams?: AccessibilityAnchorButtonParams<P>;
    rightAccessibilityButtonParams?: AccessibilityAnchorButtonParams<P>;
    accessibilityButtonPlacement?: AccessibilityButtonsPlacement;
    scrollBehavior?: () => void;
}

export default function touchBehavior<P>(props: TouchBehaviourProps<P>): React.ComponentClass<P> {
    const {
        Component,
        originalLeftSwipeRenderer,
        originalRightSwipeRenderer,
        originalGetLeftSwipeAction,
        originalGetRightSwipeAction,
        clickAction,
        threshold,
        skipHandleTouchCallback,
        longPressAction,
        originalLeftSwipeActionInitiated,
        originalRightSwipeActionInitiated,
        alwaysRenderSwipeRenderer = false,
        enableWillChangeTransform = false,
        notifyOnSwipe,
        leftAccessibilityButtonParams,
        rightAccessibilityButtonParams,
        shouldHandleRTLScenario = true,
        accessibilityButtonPlacement,
        scrollBehavior,
    } = props;
    const isLTR = !shouldHandleRTLScenario || !getRTL();

    // renderers
    const leftSwipeRenderer = isLTR ? originalLeftSwipeRenderer : originalRightSwipeRenderer;
    const rightSwipeRenderer = isLTR ? originalRightSwipeRenderer : originalLeftSwipeRenderer;

    // swipe actions
    const getLeftSwipeAction = isLTR ? originalGetLeftSwipeAction : originalGetRightSwipeAction;
    const getRightSwipeAction = isLTR ? originalGetRightSwipeAction : originalGetLeftSwipeAction;

    // initiators
    const leftSwipeActionInitiated = isLTR
        ? originalLeftSwipeActionInitiated
        : originalRightSwipeActionInitiated;
    const rightSwipeActionInitiated = isLTR
        ? originalRightSwipeActionInitiated
        : originalLeftSwipeActionInitiated;

    return class TouchComponent extends React.Component<P, TouchState> {
        private pressTimer = null;

        private isScrolling = false;
        private isLongPress = false;

        private transitionDiv: HTMLElement;
        private wrappedComponentRef = React.createRef<any>();

        constructor(props: P) {
            super(props);
            this.state = {
                started: false,
                isSwiping: false,
                startX: 0,
                startY: 0,
                moveX: 0,
                leftAction: false,
                rightAction: false,
            };
        }

        componentWillUnmount() {
            clearTimeout(this.pressTimer);
        }

        public trySetFocus = () => {
            trySetFocusOnElement(this.wrappedComponentRef, true /* customFocus */);
        };

        notifySwipeStage(swipeStage: SwipeStage) {
            notifyOnSwipe?.(this.props, swipeStage);
        }

        private executeLeftAction() {
            if (getLeftSwipeAction()) {
                if (leftSwipeActionInitiated) {
                    leftSwipeActionInitiated(this.props);
                }
                this.setState({
                    leftAction: true,
                    started: false,
                    startX: 0,
                    startY: 0,
                    moveX: 0,
                });
            }
        }

        private executeRightAction() {
            if (getRightSwipeAction()) {
                if (rightSwipeActionInitiated) {
                    rightSwipeActionInitiated(this.props);
                }
                this.setState({
                    rightAction: true,
                    started: false,
                    startX: 0,
                    startY: 0,
                    moveX: 0,
                });
            }
        }

        private onTouchStart = (evt: React.SyntheticEvent<EventTarget>) => {
            if (this.state.rightAction || this.state.leftAction) {
                // If another touch starts before left or right action finishes,
                // ignore it
                return;
            }

            const nativeEvent = evt.nativeEvent as TouchEvent;
            const touchPosition = nativeEvent.touches[0];
            const pageX = touchPosition.pageX;
            this.isLongPress = false;

            if (pageX < threshold.edgeSwipe || pageX > window.innerWidth - threshold.edgeSwipe) {
                // edge swipe
                return;
            }

            if (skipHandleTouchCallback(null, null, evt.nativeEvent)) {
                return;
            }

            if (longPressAction) {
                this.pressTimer = setTimeout(() => {
                    this.isLongPress = true;
                    longPressAction(this.props);
                }, threshold.longPress);
            }

            this.setState({
                started: true,
                startX: pageX,
                startY: touchPosition.pageY,
                isSwiping: false,
            });
        };

        // onTouchMove needs to take nativeEvent as param
        // so preventDefault can take effect on it
        private onTouchMove = (nativeEvent: TouchEvent) => {
            if (this.isScrolling || this.isLongPress || !this.state.started) {
                return;
            }

            const touchPosition = nativeEvent.changedTouches[0];
            const currentX = touchPosition.pageX;
            const moveX = currentX - this.state.startX;
            const absX = Math.abs(moveX);
            const absY = Math.abs(touchPosition.pageY - this.state.startY);

            if (
                skipHandleTouchCallback(
                    moveX,
                    this.wrappedComponentRef.current,
                    nativeEvent as Event
                )
            ) {
                return;
            }

            if (!this.state.isSwiping) {
                if (absX <= absY) {
                    this.isScrolling = true;
                    scrollBehavior?.();
                    clearTimeout(this.pressTimer);
                } else {
                    // Likely swiping, let's prevent the scroll event.
                    nativeEvent.preventDefault();
                    nativeEvent.stopImmediatePropagation();

                    if (absX > threshold.swipe) {
                        this.notifySwipeStage(SwipeStage.Started);

                        this.setState({ isSwiping: true });
                        clearTimeout(this.pressTimer);
                    }
                }
            } else {
                // We are swiping, let's prevent the scroll event.
                nativeEvent.preventDefault();
                nativeEvent.stopImmediatePropagation();

                this.setState({ moveX: moveX });
            }
        };

        private onTouchEnd = (evt: React.SyntheticEvent<EventTarget>) => {
            if (!this.state.started) {
                return;
            }

            clearTimeout(this.pressTimer);
            this.isScrolling = false;

            if (Math.abs(this.state.moveX) > threshold.action) {
                if (this.state.moveX > 0) {
                    this.executeLeftAction();
                } else {
                    this.executeRightAction();
                }
            } else {
                this.state.isSwiping && this.notifySwipeStage(SwipeStage.Cancelled);
                this.setState({
                    started: false,
                    isSwiping: false,
                    startX: 0,
                    startY: 0,
                    moveX: 0,
                });
            }
        };

        private onClick = (evt: React.MouseEvent<EventTarget>) => {
            if (
                this.isLongPress ||
                this.state.rightAction ||
                this.state.leftAction ||
                !clickAction
            ) {
                return;
            }

            clickAction(this.props);
        };

        private setTransitionDiv = (ref: HTMLElement) => {
            if (this.transitionDiv) {
                this.transitionDiv.removeEventListener('transitionend', this.onTransitionEnd);
                this.transitionDiv.removeEventListener('touchmove', this.onTouchMove);
            }

            this.transitionDiv = ref;
            if (this.transitionDiv) {
                this.transitionDiv.addEventListener('transitionend', this.onTransitionEnd);
                this.transitionDiv.addEventListener('touchmove', this.onTouchMove);
            }
        };

        private onTransitionEnd = () => {
            if (this.state.rightAction && getRightSwipeAction()) {
                getRightSwipeAction()(this.props);
            } else if (this.state.leftAction && getLeftSwipeAction()) {
                getLeftSwipeAction()(this.props);
            }

            this.notifySwipeStage(SwipeStage.Completed);

            this.setState({
                leftAction: false,
                rightAction: false,
                isSwiping: false,
            });
        };

        render() {
            let translate = null;
            if (this.state.leftAction) {
                translate = { transform: 'translate3d(100%, 0, 0)', transition: 'transform 200ms' };
            } else if (this.state.rightAction) {
                translate = {
                    transform: 'translate3d(-100%, 0, 0)',
                    transition: 'transform 200ms',
                };
            } else if (
                (this.state.moveX > 0 && getLeftSwipeAction()) ||
                (this.state.moveX < 0 && getRightSwipeAction())
            ) {
                translate = {
                    transform: 'translate3d(' + this.state.moveX + 'px, 0, 0)',
                    transition: 'none',
                };
            }

            if (enableWillChangeTransform) {
                if (translate) {
                    /* tslint:disable */
                    translate['touchAction'] = 'none';
                    translate['willChange'] = 'transform';
                    /* tslint:enable */
                } else {
                    translate = { touchAction: 'none', willChange: 'transform' };
                }
            }

            const leftAccessibilityButton: JSX.Element = leftAccessibilityButtonParams && (
                <AccessibilityAnchorButton
                    key="leftAccessibilityButton"
                    orientation={AccessibilityAnchorOrientation.Left}
                    onClick={() =>
                        leftAccessibilityButtonParams.onClick
                            ? leftAccessibilityButtonParams.onClick(this.props)
                            : getLeftSwipeAction()(this.props)
                    }
                    announcementOnClick={
                        leftAccessibilityButtonParams.announcementGenerator
                            ? leftAccessibilityButtonParams.announcementGenerator(this.props)
                            : ''
                    }
                    label={
                        leftAccessibilityButtonParams.labelGenerator
                            ? leftAccessibilityButtonParams.labelGenerator(this.props)
                            : ''
                    }
                />
            );
            const component: JSX.Element = (!Component.prototype?.render && (
                <Component key="wrappedComponent" {...this.props} />
            )) || (
                <Component key="wrappedComponent" ref={this.wrappedComponentRef} {...this.props} />
            );
            const rightAccessibilityButton: JSX.Element = rightAccessibilityButtonParams && (
                <AccessibilityAnchorButton
                    key="rightAccessibilityButton"
                    orientation={AccessibilityAnchorOrientation.Right}
                    onClick={() =>
                        rightAccessibilityButtonParams.onClick
                            ? rightAccessibilityButtonParams.onClick(this.props)
                            : getLeftSwipeAction()(this.props)
                    }
                    announcementOnClick={
                        rightAccessibilityButtonParams.announcementGenerator
                            ? rightAccessibilityButtonParams.announcementGenerator(this.props)
                            : ''
                    }
                    label={
                        rightAccessibilityButtonParams.labelGenerator
                            ? rightAccessibilityButtonParams.labelGenerator(this.props)
                            : ''
                    }
                />
            );

            /**
             * The reordering of accessibility buttons are done based on the requirement in day view swipe a11y
             * when the boolean value is true then the order will be [component, leftA11yButton, rightA11yButton]
             * when the boolean is false the order will be [leftA11yButton, component, rightA11yButton]
             */
            let childComponents = null;
            switch (accessibilityButtonPlacement) {
                case AccessibilityButtonsPlacement.BEFORE:
                    childComponents = [
                        leftAccessibilityButton,
                        rightAccessibilityButton,
                        component,
                    ];
                    break;
                case AccessibilityButtonsPlacement.AFTER:
                    childComponents = [
                        component,
                        leftAccessibilityButton,
                        rightAccessibilityButton,
                    ];
                    break;
                default:
                    childComponents = [
                        leftAccessibilityButton,
                        component,
                        rightAccessibilityButton,
                    ];
            }
            childComponents.filter(Boolean);
            !isLTR && childComponents.reverse();

            return (
                <div
                    role="group"
                    ref={this.setTransitionDiv}
                    style={translate}
                    onTouchStart={this.onTouchStart}
                    onTouchEnd={this.onTouchEnd}
                    onClick={this.onClick}>
                    {(this.state.isSwiping || alwaysRenderSwipeRenderer) &&
                        leftSwipeRenderer(this.props, this.state, threshold)}
                    {childComponents.map(component => component)}
                    {(this.state.isSwiping || alwaysRenderSwipeRenderer) &&
                        rightSwipeRenderer(this.props, this.state, threshold)}
                </div>
            );
        }
    };
}
